import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Chip,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import {
  ContentCopy,
  NavigateNext,
  AutoAwesome,
  Check,
} from '@mui/icons-material';
import {
  useAIReferences,
  useAITemplates,
  useCreateAIQueueItem,
  useIncrementTemplateUsage,
} from '../../hooks/useAIGeneration';
import type { AIReferenceSong, AIPromptTemplate } from '../../api/types';
import { useOrgFilter } from '../../contexts/OrganizationContext';

export const GeneratorSection: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedReference, setSelectedReference] = useState<AIReferenceSong | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<AIPromptTemplate | null>(null);
  const [analysisResponse, setAnalysisResponse] = useState('');
  const [stylePrompt, setStylePrompt] = useState('');
  const [theme, setTheme] = useState('');
  const [targetGenre, setTargetGenre] = useState('');
  const [targetMood, setTargetMood] = useState('');
  const [targetEnergy, setTargetEnergy] = useState(5);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const orgFilter = useOrgFilter();

  const { data: references, isLoading: loadingRefs } = useAIReferences({ organization_id: orgFilter });
  const { data: templates } = useAITemplates({ organization_id: orgFilter, is_active: true });
  const createQueueMutation = useCreateAIQueueItem();
  const incrementUsageMutation = useIncrementTemplateUsage();

  const generateAnalysisPrompt = () => {
    if (selectedTemplate?.claude_prompt_template && selectedReference) {
      return selectedTemplate.claude_prompt_template
        .replace('{{title}}', selectedReference.title)
        .replace('{{artist}}', selectedReference.artist)
        .replace('{{reason}}', selectedReference.reason || 'General inspiration');
    }

    if (!selectedReference) return '';

    return `Analyze the following song for AI music generation purposes:

Song: "${selectedReference.title}" by ${selectedReference.artist}
${selectedReference.reason ? `Reason for reference: ${selectedReference.reason}` : ''}

Please provide:
1. Musical key and mode
2. BPM estimate
3. Time signature
4. Main chord progression
5. Genre classification
6. Mood/atmosphere description
7. Energy level (1-10)
8. Notable production elements
9. Suggested style prompt for AI music generation

Format the style prompt as a single paragraph that captures the essence of the song's style.`;
  };

  const handleCopyAnalysisPrompt = () => {
    const prompt = generateAnalysisPrompt();
    navigator.clipboard.writeText(prompt);
    setSnackbarMessage('Analysis prompt copied! Paste it into Claude Code.');
    setSnackbarOpen(true);
  };

  const handleCopyStylePrompt = () => {
    navigator.clipboard.writeText(stylePrompt);
    setSnackbarMessage('Style prompt copied! Paste it into your AI music generator.');
    setSnackbarOpen(true);
  };

  const handleExtractStylePrompt = () => {
    // Try to extract style prompt from analysis response
    const lines = analysisResponse.split('\n');
    let extractedPrompt = '';

    // Look for style/suno prompt section
    let inPromptSection = false;
    for (const line of lines) {
      if (line.toLowerCase().includes('style prompt') || line.toLowerCase().includes('suno prompt') || line.toLowerCase().includes('suggested prompt')) {
        inPromptSection = true;
        continue;
      }
      if (inPromptSection && line.trim()) {
        extractedPrompt = line.trim();
        break;
      }
    }

    if (extractedPrompt) {
      setStylePrompt(extractedPrompt);
      setSnackbarMessage('Style prompt extracted from analysis');
      setSnackbarOpen(true);
    } else {
      // If no extraction possible, use the whole response or generate from template
      if (selectedTemplate?.style_prompt_template) {
        const generated = selectedTemplate.style_prompt_template
          .replace('{{mood}}', targetMood || 'uplifting')
          .replace('{{tempo}}', 'moderate')
          .replace('{{key}}', selectedReference?.musical_key || 'C major')
          .replace('{{instruments}}', 'modern production')
          .replace('{{energy}}', targetEnergy.toString())
          .replace('{{bpm}}', selectedReference?.bpm?.toString() || '120')
          .replace('{{subgenre}}', targetGenre || 'pop')
          .replace('{{elements}}', 'synthesizers and bass')
          .replace('{{style}}', 'folk');
        setStylePrompt(generated);
      }
      setSnackbarMessage('Could not extract - please write or paste the style prompt manually');
      setSnackbarOpen(true);
    }
  };

  const handleAddToQueue = async () => {
    try {
      await createQueueMutation.mutateAsync({
        organization_id: orgFilter || '',
        reference_song_id: selectedReference?.id,
        theme: theme || `Based on ${selectedReference?.title}`,
        target_genre: targetGenre || selectedReference?.genre,
        target_mood: targetMood || selectedReference?.mood,
        target_energy: targetEnergy,
        style_prompt: stylePrompt,
        status: 'prompt_ready',
      });

      if (selectedTemplate) {
        await incrementUsageMutation.mutateAsync(selectedTemplate.id);
      }

      setSnackbarMessage('Added to generation queue!');
      setSnackbarOpen(true);

      // Reset for next generation
      setActiveStep(0);
      setSelectedReference(null);
      setAnalysisResponse('');
      setStylePrompt('');
      setTheme('');
    } catch (err) {
      console.error('Failed to add to queue:', err);
    }
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  if (loadingRefs) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {/* Step 1: Select Reference */}
          <Step>
            <StepLabel>Select Reference Song (Optional)</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose a reference song to base your generation on, or skip to create from scratch.
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Reference Song</InputLabel>
                <Select
                  value={selectedReference?.id || ''}
                  label="Reference Song"
                  onChange={(e) => {
                    const ref = references?.find((r) => r.id === e.target.value);
                    setSelectedReference(ref || null);
                    if (ref) {
                      setTargetGenre(ref.genre || '');
                      setTargetMood(ref.mood || '');
                      setTargetEnergy(ref.energy_level || 5);
                    }
                  }}
                >
                  <MenuItem value="">None (Create from scratch)</MenuItem>
                  {references?.map((ref) => (
                    <MenuItem key={ref.id} value={ref.id}>
                      {ref.title} - {ref.artist}
                      {ref.genre && <Chip label={ref.genre} size="small" sx={{ ml: 1 }} />}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!selectedReference && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Or describe what you want:</Typography>
                  <TextField
                    label="Theme / Description"
                    value={theme}
                    onChange={(e) => setTheme(e.target.value)}
                    placeholder="e.g., Upbeat summer pop song with tropical vibes"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      label="Genre"
                      value={targetGenre}
                      onChange={(e) => setTargetGenre(e.target.value)}
                      placeholder="e.g., Pop"
                      fullWidth
                    />
                    <TextField
                      label="Mood"
                      value={targetMood}
                      onChange={(e) => setTargetMood(e.target.value)}
                      placeholder="e.g., Happy"
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography>Energy:</Typography>
                    <Rating
                      value={targetEnergy}
                      max={10}
                      onChange={(_, value) => setTargetEnergy(value || 5)}
                    />
                    <Typography color="text.secondary">({targetEnergy}/10)</Typography>
                  </Box>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Button variant="contained" onClick={handleNext}>
                  {selectedReference ? 'Continue with Reference' : 'Continue without Reference'}
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 2: Copy Analysis Prompt */}
          <Step>
            <StepLabel>Copy Analysis Prompt for Claude</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Copy this prompt and paste it into Claude Code to analyze the reference song.
              </Typography>

              {templates && templates.length > 0 && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Template (Optional)</InputLabel>
                  <Select
                    value={selectedTemplate?.id || ''}
                    label="Template (Optional)"
                    onChange={(e) => {
                      const tpl = templates?.find((t) => t.id === e.target.value);
                      setSelectedTemplate(tpl || null);
                    }}
                  >
                    <MenuItem value="">Default Template</MenuItem>
                    {templates?.map((tpl) => (
                      <MenuItem key={tpl.id} value={tpl.id}>
                        {tpl.name}
                        {tpl.is_system && <Chip label="System" size="small" sx={{ ml: 1 }} />}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Card variant="outlined" sx={{ mb: 2, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}
                  >
                    {generateAnalysisPrompt() || 'Enter a theme or select a reference to generate a prompt.'}
                  </Typography>
                </CardContent>
              </Card>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={handleBack}>Back</Button>
                <Button
                  variant="contained"
                  startIcon={<ContentCopy />}
                  onClick={handleCopyAnalysisPrompt}
                  disabled={!selectedReference && !theme}
                >
                  Copy Prompt
                </Button>
                <Button variant="outlined" onClick={handleNext}>
                  Skip (I already have analysis)
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 3: Paste Analysis */}
          <Step>
            <StepLabel>Paste Analysis & Extract Style Prompt</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Paste Claude's analysis response here. The system will try to extract the style prompt.
              </Typography>

              <TextField
                label="Claude's Analysis Response"
                value={analysisResponse}
                onChange={(e) => setAnalysisResponse(e.target.value)}
                multiline
                rows={8}
                placeholder="Paste the full analysis response from Claude..."
                fullWidth
                sx={{ mb: 2 }}
              />

              <Button
                variant="outlined"
                startIcon={<AutoAwesome />}
                onClick={handleExtractStylePrompt}
                disabled={!analysisResponse}
                sx={{ mb: 2 }}
              >
                Extract Style Prompt
              </Button>

              <TextField
                label="Style Prompt"
                value={stylePrompt}
                onChange={(e) => setStylePrompt(e.target.value)}
                multiline
                rows={3}
                placeholder="The optimized style prompt for AI generation..."
                fullWidth
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={handleBack}>Back</Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!stylePrompt}
                  endIcon={<NavigateNext />}
                >
                  Continue
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 4: Copy Style Prompt */}
          <Step>
            <StepLabel>Copy Style Prompt</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Copy this prompt and paste it into your AI music generator.
              </Typography>

              <Card variant="outlined" sx={{ mb: 2, bgcolor: 'primary.50' }}>
                <CardContent>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {stylePrompt}
                  </Typography>
                </CardContent>
              </Card>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={handleBack}>Back</Button>
                <Button
                  variant="contained"
                  startIcon={<ContentCopy />}
                  onClick={() => {
                    handleCopyStylePrompt();
                    handleNext();
                  }}
                >
                  Copy & Continue
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 5: Add to Queue */}
          <Step>
            <StepLabel>Add to Queue</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                After generating your song, add this to your queue to track it.
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                Use your AI music generator, paste the prompt, generate your track, then come back to upload it.
              </Alert>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={handleBack}>Back</Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Check />}
                  onClick={handleAddToQueue}
                  disabled={createQueueMutation.isPending}
                >
                  Add to Queue
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default GeneratorSection;
